// Joining submissions to their decisions, client-side.
//
// `kyc_submission_reviews` is append-only and `set_kyc_status` does not refuse a
// second decision on the same submission — a client verified in error can be
// rejected later against that same photo. So a submission maps to a LIST of
// decisions, and the current one is the newest. Taking the first row found
// would show a reviewer the state that was reversed.

export type KycDecision = "verified" | "rejected";

/** The shape the submissions query selects. `storage_path` is absent because no
 *  role — superadmin included — is granted it (0070). */
export interface KycSubmissionRecord {
  id: string;
  client_id: string;
  declared_document_type: string;
  byte_size: number;
  /** BL-43 — null for a submission made before the selfie step (0291 stopgap). */
  selfie_byte_size?: number | null;
  purged_at: string | null;
  created_at: string | null;
  profiles: { full_name: string; phone: string | null; kyc_status: string } | null;
}

export interface KycReviewRecord {
  submission_id: string;
  decision: KycDecision;
  created_at: string | null;
}

export interface KycQueueRow {
  id: string;
  clientId: string;
  clientName: string;
  clientPhone: string | null;
  /** The client's CURRENT status, which may reflect a newer submission. */
  clientKycStatus: string;
  declaredDocumentType: string;
  byteSize: number;
  /** Whether a selfie came with it. Stopgap (0291) submissions have none. */
  hasSelfie: boolean;
  submittedAt: string | null;
  /** Non-null once the retention purge has removed the bytes (OQ-83). */
  purgedAt: string | null;
  decision: KycDecision | null;
  decidedAt: string | null;
}

function newest(a: KycReviewRecord, b: KycReviewRecord): KycReviewRecord {
  return (b.created_at ?? "") > (a.created_at ?? "") ? b : a;
}

export function buildKycQueue(
  submissions: readonly KycSubmissionRecord[],
  reviews: readonly KycReviewRecord[],
): KycQueueRow[] {
  const latest = new Map<string, KycReviewRecord>();
  for (const review of reviews) {
    const current = latest.get(review.submission_id);
    latest.set(review.submission_id, current ? newest(current, review) : review);
  }

  return submissions.map((submission) => {
    const review = latest.get(submission.id) ?? null;
    return {
      id: submission.id,
      clientId: submission.client_id,
      // A submission cannot outlive its client (0071 keeps the FK), so a null
      // join means the row was filtered by RLS rather than deleted.
      clientName: submission.profiles?.full_name ?? "Unknown client",
      clientPhone: submission.profiles?.phone ?? null,
      clientKycStatus: submission.profiles?.kyc_status ?? "none",
      declaredDocumentType: submission.declared_document_type,
      byteSize: submission.byte_size,
      hasSelfie: submission.selfie_byte_size != null,
      submittedAt: submission.created_at,
      purgedAt: submission.purged_at,
      decision: review?.decision ?? null,
      decidedAt: review?.created_at ?? null,
    };
  });
}

/** The actual work: a photo nobody has decided on yet. */
export function isAwaitingReview(row: KycQueueRow): boolean {
  return row.decision === null;
}

/** Bytes → a short human string. Reviewers use it to spot a photo too small to
 *  be legible before opening it. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
